import React, { useEffect, useState } from "react";
import OrderForm, { type GenericFieldValidationType } from "../../OrderForm";
import "./SpecForm.scss";
import { Modal } from "components";
import { Collapsible } from "components/Collapsible";
import { ManagedInput } from "components/orders/Fields/Input/ManagedInput";
import { ManagedSelect } from "components/orders/Fields/Select/ManagedSelect";
import { ManagedToggle } from "components/orders/Fields/Toggle/ManagedToggle";
import UserModalActions from "components/user/UserModalActions";
import { FeatureType, OrderType } from "models";
import { Button } from "semantic-ui-react";
import Obstruction from "./ObstructionFeat";
import OutsideStep from "./OutsideStepFeat";
import PartialDeckingFeat from "./PartialDecking";
import { SolidShallowEndOptions, getFeatureTypeOptions } from "./SpecConstants";
import {
  type CoverFeatureAttributes,
  type CoverMaterialAttributes,
  type LinerAccessoryAttributes,
  type LinerMaterialAttributes,
  type ObstructionTypeArray,
  type OutsideStepArray,
  type PartialDecking,
  type SpecFormFieldsValidationType,
  type SpecFormPropTypes,
  SpecFormSchema,
  type VinylCoveredSteps,
  type VinylCoveredStepsInfo,
} from "./SpecFormSchema";
import VinylCoveredStepInfo from "./VinylCoveredStep";
import { OrderContext } from "../../../../../context/OrderContext";
import { SafetyStripesOptions } from "../Accessories/AccessoriesFormConstants";
import { useT } from "i18n";

interface SpecFormProps {
  data: SpecFormPropTypes;
  hasEditAccess: boolean;
}

/**
 * All fields are "valid", until we get an error!
 */
const InitialSpecFormValidationStatus = (): SpecFormFieldsValidationType => {
  return {
    spec: true,
  };
};

// Titled Order Data in Form Sidebar
const SpecForm = (props: SpecFormProps) => {
  const t = useT();
  // Global state
  const { state, dispatch } = React.useContext(OrderContext);
  const isCover = state.project.type === OrderType.Cover;
  const [featureType, setFeatureType] = useState("");
  const [featModalOpen, setFeatModalOpen] = useState(false);
  const [newFeature, setNewFeature] = useState<
    ObstructionTypeArray | OutsideStepArray
  >();

  const featureTypeOptions = getFeatureTypeOptions();

  const disabled = state?.order?.archived || !props?.hasEditAccess;

  // complete spec object
  const [spec, setSpec] = useState(props?.data);

  // #region coverFeatureAttributes
  const [coverFeatureAttributes] = useState<CoverFeatureAttributes>(
    props?.data?.coverFeatureAttributes,
  );
  const [obstructionTypeArray] = useState(
    props?.data?.coverFeatureAttributes?.obstructionTypeArray,
  );
  const [outsideStepArray] = useState(
    props?.data?.coverFeatureAttributes?.outsideStepArray,
  );
  // #endregion

  // #region vinylCoveredSteps
  const [vinylCoveredSteps] = useState<VinylCoveredSteps>(
    props?.data?.vinylCoveredSteps,
  );
  const [vinylCoveredStepsInfo] = useState(props?.data?.vinylCoveredSteps?.vos);
  // #endregion

  // #region coverMaterialAttributes
  const [coverMaterialAttributes, setCoverMaterialAttributes] =
    useState<CoverMaterialAttributes>(props.data.coverMaterialAttributes);
  const [generalSpecific, setGeneralSpecific] = useState<string | undefined>(
    state?.spec?.coverMaterialAttributes?.generalSpecific ??
      props?.data?.coverMaterialAttributes?.generalSpecific ??
      "",
  );
  const [materialColor, setMaterialColor] = useState<string | undefined>(
    state?.spec?.coverMaterialAttributes?.materialColor ??
      props?.data?.coverMaterialAttributes?.materialColor ??
      "",
  );
  const [materialDrainTypeFeatureSId, setMaterialDrainTypeFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.coverMaterialAttributes?.materialColor ??
      props?.data?.coverMaterialAttributes?.materialDrainTypeFeatureSId ??
      "",
  );
  const [materialFabric, setMaterialFabric] = useState<string | undefined>(
    state?.spec?.coverMaterialAttributes?.materialFabric ??
      props?.data?.coverMaterialAttributes?.materialFabric ??
      "",
  );
  const [materialGrid, setMaterialGrid] = useState<string | undefined>(
    state?.spec?.coverMaterialAttributes?.materialGrid?.toString() ??
      props?.data?.coverMaterialAttributes?.materialGrid?.toString(),
  );
  const [materialSpringUpgradeFeatureSId, setMaterialSpringUpgradeFeatureSId] =
    useState<string | undefined>(
      state?.spec?.coverMaterialAttributes?.materialSpringUpgradeFeatureSId ??
        props?.data?.coverMaterialAttributes?.materialSpringUpgradeFeatureSId ??
        "",
    );
  // #endregion

  // #region linerMaterialAttributes
  const [linerMaterialAttributes, setLinerMaterialAttributes] =
    useState<LinerMaterialAttributes>(props.data.linerMaterialAttributes);

  const [materialWall, setMaterialWall] = useState<string | undefined>(
    state?.spec?.linerMaterialAttributes?.materialWall ??
      props?.data?.linerMaterialAttributes?.materialWall ??
      "",
  );
  const [materialFloor, setMaterialFloor] = useState<string | undefined>(
    state?.spec?.linerMaterialAttributes?.materialFloor ??
      props?.data?.linerMaterialAttributes?.materialFloor ??
      "",
  );
  const [materialBeadType, setMaterialBeadType] = useState<string | undefined>(
    state?.spec?.linerMaterialAttributes?.materialBeadType ??
      props?.data?.linerMaterialAttributes?.materialBeadType ??
      "",
  );
  // #endregion

  // #region linerAccessoryAttributes
  const [linerAccessoryAttributes, setlinerAccessoryAttributes] =
    useState<LinerAccessoryAttributes>(props.data.linerAccessoryAttributes);

  const [safetyStripFeatureSId, setSafetyStripFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.linerAccessoryAttributes?.safetyStripFeatureSId ??
      props.data.linerAccessoryAttributes.safetyStripFeatureSId,
  );

  const [safetyStripeWidth, setSafetyStripeWidth] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.safetyStripeWidth?.toString() ??
      props.data.linerAccessoryAttributes.safetyStripeWidth?.toString(),
  );

  const [materialSolidShallowFeatureSId, setMaterialSolidShallowFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.linerAccessoryAttributes?.materialSolidShallowFeatureSId ??
      props.data.linerAccessoryAttributes.materialSolidShallowFeatureSId,
  );

  const [safetyStripeColor, setSafetyStripeColor] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.safetyStripeColor ??
      props.data.linerAccessoryAttributes.safetyStripeColor,
  );

  const [materialOverlapFeatureId, setMaterialOverlapFeatureId] = useState<
    string | undefined
  >(
    state?.spec?.linerAccessoryAttributes?.materialOverlapFeatureId ??
      props.data.linerAccessoryAttributes.materialOverlapFeatureId,
  );

  const [materialOverlapSize, setMaterialOverlapSize] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.materialOverlapSize?.toString() ??
      props.data.linerAccessoryAttributes.materialOverlapSize?.toString() ??
      "0",
  );

  const [materialSolidShallowColor, setMaterialSolidShallowColor] = useState<
    string | undefined
  >(
    state?.spec?.linerAccessoryAttributes?.materialSolidShallowColor ??
      props.data.linerAccessoryAttributes.materialSolidShallowColor,
  );

  const [racingLanesFeatureSId, setRacingLanesFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.linerAccessoryAttributes?.racingLanesFeatureSId ??
      props.data.linerAccessoryAttributes.racingLanesFeatureSId,
  );

  const [racingLanesComments, setRacingLanesComments] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.racingLanesComments ??
      props.data.linerAccessoryAttributes.racingLanesComments,
  );

  const [targetFeatureSId, setTargetFeatureSId] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.targetFeatureSId ??
      props.data.linerAccessoryAttributes.targetFeatureSId,
  );

  const [targetComments, setTargetComments] = useState<string | undefined>(
    state?.spec?.linerAccessoryAttributes?.targetComments ??
      props.data.linerAccessoryAttributes.targetComments,
  );
  // #endregion

  // #region customerAttributes
  const [customerAttributes, setCustomerAttributes] = useState(
    props?.data?.customerAttributes,
  );
  const [sugarId, setSugarId] = useState<string | undefined>(
    props?.data?.customerAttributes?.SugarId,
  );
  const [customerName, setCustomerName] = useState<string | undefined>(
    props?.data?.customerAttributes?.customerName,
  );
  const [gl_company_new_c, setGl_company_new_c] = useState<string | undefined>(
    props?.data?.customerAttributes?.gl_company_new_c,
  );
  const [indirect_customer_number_c, setIndirect_customer_number_c] = useState<
    string | undefined
  >(props?.data?.customerAttributes?.indirect_customer_number_c);
  const [purchasing_channel_c, setPurchasing_channel_c] = useState<string | undefined>(
    props?.data?.customerAttributes?.purchasing_channel_c,
  );
  const [rewards_id_c, setRewards_id_c] = useState<string | undefined>(
    props?.data?.customerAttributes?.rewards_id_c,
  );
  const [selectedBranchNumber, setSelectedBranchNumber] = useState<string | undefined>(
    props?.data?.customerAttributes?.selectedBranchNumber,
  );
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState<
    string | undefined
  >(props?.data?.customerAttributes?.selectedCustomerNumber);
  // #endregion

  // #region deckingAttributes
  const [deckingAttributes, setDeckingAttributes] = useState(
    props?.data?.deckingAttributes,
  );
  const [copingTexture, setCopingTexture] = useState<string | undefined>(
    state.spec?.deckingAttributes?.copingTexture ||
      props?.data?.deckingAttributes?.copingTexture,
  );
  const [deckAnchorFeatureSId, setDeckAnchorFeatureSId] = useState<string | undefined>(
    state?.spec?.deckingAttributes?.deckAnchorFeatureSId ||
      props?.data?.deckingAttributes?.deckAnchorFeatureSId,
  );
  const [deckConsistency, setDeckConsistency] = useState(
    props?.data?.deckingAttributes?.deckConsistency ?? false,
  );
  const [deckSize, setDeckSize] = useState<string | undefined>(
    props?.data?.deckingAttributes?.deckSize?.toString(),
  );
  const [deckType, setDeckType] = useState<string | undefined>(
    state.spec?.deckingAttributes?.deckType || props?.data?.deckingAttributes?.deckType,
  );
  const [extraPaddingFeatureId, setExtraPaddingFeatureId] = useState<
    string | undefined
  >(
    state.spec?.deckingAttributes?.extraPaddingFeatureId ||
      props?.data?.deckingAttributes?.extraPaddingFeatureId,
  );
  const [extraPaddingLength, setExtraPaddingLength] = useState<string | undefined>(
    props?.data?.deckingAttributes?.extraPaddingLength?.toString(),
  );
  const [partialDeckingArray] = useState(
    props?.data?.deckingAttributes?.partialDeckingArray,
  );
  // #endregion

  // #region fullPerimeterCoverAccessoryAttributes
  const [
    fullPerimeterCoverAccessoryAttributes,
    setFullPerimeterCoverAccessoryAttributes,
  ] = useState(props?.data?.fullPerimeterCoverAccessoryAttributes);
  const [brassAnchorCollarsFeatureSId, setBrassAnchorCollarsFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.brassAnchorCollarsFeatureSId ||
      props?.data?.fullPerimeterCoverAccessoryAttributes?.brassAnchorCollarsFeatureSId,
  );
  const [bucklesFeatureSId, setBucklesFeatureSId] = useState<string | undefined>(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.bucklesFeatureSId ??
      props?.data?.fullPerimeterCoverAccessoryAttributes?.bucklesFeatureSId,
  );
  const [cableArea1FeatureId, setCableArea1FeatureId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea1
      ?.cableArea1FeatureId,
  );
  const [cableArea1Length, setCableArea1Length] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea1?.cableArea1Length?.toString(),
  );
  const [cableArea2FeatureId, setCableArea2FeatureId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea2
      ?.cableArea2FeatureId,
  );
  const [cableArea2Length, setCableArea2Length] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea2?.cableArea2Length?.toString(),
  );
  const [cableArea3FeatureId, setCableArea3FeatureId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea3
      ?.cableArea3FeatureId,
  );
  const [cableArea3Length, setCableArea3Length] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea3?.cableArea3Length?.toString(),
  );
  const [cableArea4FeatureId, setCableArea4FeatureId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea4
      ?.cableArea4FeatureId,
  );
  const [cableArea4Length, setCableArea4Length] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.cableAssembly?.cableArea4?.cableArea4Length?.toString(),
  );

  const [doubleDRingsFeatureSId, setDoubleDRingsFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.doubleDRingsFeatureSId ??
      props?.data?.fullPerimeterCoverAccessoryAttributes?.doubleDRingsFeatureSId,
  );
  const [extraLongAnchorsFeatureSId, setExtraLongAnchorsFeatureSId] = useState<
    string | undefined
  >(props?.data?.fullPerimeterCoverAccessoryAttributes?.extraLongAnchorsFeatureSId);
  const [kemKapFeatureSId, setKemKapFeatureSId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.kemKapFeatureSId,
  );
  const [noAnchorsFeatureSId, setNoAnchorsFeatureSId] = useState<string | undefined>(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.noAnchorsFeatureSId ||
      props?.data?.fullPerimeterCoverAccessoryAttributes?.noAnchorsFeatureSId,
  );
  const [noHardwareFeatureSId, setNoHardwareFeatureSId] = useState<string | undefined>(
    state?.spec?.fullPerimeterCoverAccessoryAttributes.noHardwareFeatureSId ||
      props?.data?.fullPerimeterCoverAccessoryAttributes?.noHardwareFeatureSId,
  );
  const [topBottomWebbingFeatureSId, setTopBottomWebbingFeatureSId] = useState<
    string | undefined
  >(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.topBottomWebbingFeatureSId ??
      props?.data?.fullPerimeterCoverAccessoryAttributes?.topBottomWebbingFeatureSId,
  );
  const [yStrapFeatureId, setYStrapFeatureId] = useState<string | undefined>(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.yStrapAssembly
      ?.yStrapFeatureId ??
      props?.data?.fullPerimeterCoverAccessoryAttributes?.yStrapAssembly
        ?.yStrapFeatureId,
  );
  const [yStrapQty, setYStrapQty] = useState<string | undefined>(
    state?.spec?.fullPerimeterCoverAccessoryAttributes?.yStrapAssembly?.yStrapQty?.toString() ??
      props?.data?.fullPerimeterCoverAccessoryAttributes?.yStrapAssembly?.yStrapQty?.toString(),
  );
  const [singlePadFeatureId, setSinglePadFeatureId] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.singlePad?.singlePadFeatureId,
  );
  const [singlePadQty, setSinglePadQty] = useState<string | undefined>(
    props?.data?.fullPerimeterCoverAccessoryAttributes?.singlePad?.singlePadQty?.toString(),
  );
  // #endregion

  //  #region geoShapeAttributes
  const [geoShapeAttributes, setGeoShapeAttributes] = useState(
    props?.data?.geoShapeAttributes,
  );
  const [cornerLCCornerType, setCornerLCCornerType] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLC?.cornerType,
  );
  const [cornerLCCornerSize, setCornerLCCornerSize] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLC?.cornerSize?.toString(),
  );
  const [cornerLDCornerType, setCornerLDCornerType] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLD?.cornerType,
  );
  const [cornerLDCornerSize, setCornerLDCornerSize] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLD?.cornerSize?.toString(),
  );
  const [cornerLSCornerType, setCornerLSCornerType] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLS?.cornerType,
  );
  const [cornerLSCornerSize, setCornerLSCornerSize] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerLS?.cornerSize?.toString(),
  );
  const [cornerRDCornerType, setCornerRDCornerType] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerRD?.cornerType,
  );
  const [cornerRDCornerSize, setCornerRDCornerSize] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerRD?.cornerSize?.toString(),
  );
  const [cornerRSCornerType, setCornerRSCornerType] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerRS?.cornerType,
  );
  const [cornerRSCornerSize, setCornerRSCornerSize] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cornerRS?.cornerSize?.toString(),
  );
  const [cornerReverseCornerType, setCornerReverseCornerType] = useState<
    string | undefined
  >(props?.data?.geoShapeAttributes?.cornerReverse?.cornerType);
  const [cornerReverseCornerSize, setCornerReverseCornerSize] = useState<
    string | undefined
  >(props?.data?.geoShapeAttributes?.cornerReverse?.cornerSize?.toString());
  const [cutCornerQty, setCutCornerQty] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cutCorner?.cutCornerQty?.toString(),
  );
  const [cutCornerFeatureId, setCutCornerFeatureId] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.cutCorner?.cutCornerFeatureId,
  );
  const [ovalEndQty, setOvalEndQty] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.ovalEnd?.ovalEndQty?.toString(),
  );
  const [ovalEndFeatureId, setOvalEndFeatureId] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.ovalEnd?.ovalEndFeatureId,
  );
  const [romanEndQty, setRomanEndQty] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.romanEnd?.romanEndQty?.toString(),
  );
  const [romanEndFeatureId, setRomanEndFeatureId] = useState<string | undefined>(
    props?.data?.geoShapeAttributes?.romanEnd?.romanEndFeatureId,
  );
  //  #endregion

  // #region measurementAttributes
  const [measurementAttributes, setMeasurementAttributes] = useState(
    props?.data?.measurementAttributes,
  );
  const [coverLnFt, setCoverLnFt] = useState<string | undefined>(
    props?.data?.measurementAttributes?.coverLnFt?.toString(),
  );
  const [coverSqFt, setCoverSqFt] = useState<string | undefined>(
    props?.data?.measurementAttributes?.coverSqFt?.toString(),
  );
  const [dimensionA, setDimensionA] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionA?.toString(),
  );
  const [dimensionA1, setDimensionA1] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionA1?.toString(),
  );
  const [dimensionB, setDimensionB] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionB?.toString(),
  );
  const [dimensionB3, setDimensionB3] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionB3?.toString(),
  );
  const [dimensionB7, setDimensionB7] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionB7?.toString(),
  );
  const [dimensionC, setDimensionC] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionC?.toString(),
  );
  const [dimensionD, setDimensionD] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionD?.toString(),
  );
  const [dimensionE, setDimensionE] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionE?.toString(),
  );
  const [dimensionJ2, setDimensionJ2] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionJ2?.toString(),
  );
  const [dimensionJ3, setDimensionJ3] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionJ3?.toString(),
  );
  const [dimensionJ4, setDimensionJ4] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionJ4?.toString(),
  );
  const [dimensionL, setDimensionL] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionL?.toString(),
  );
  const [dimensionRd, setDimensionRd] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionRd?.toString(),
  );
  const [dimensionS, setDimensionS] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionS?.toString(),
  );
  const [dimensionS1, setDimensionS1] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionS1?.toString(),
  );
  const [dimensionT, setDimensionT] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionT?.toString(),
  );
  const [dimensionV3, setDimensionV3] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionV3?.toString(),
  );
  const [dimensionW1, setDimensionW1] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionW1?.toString(),
  );
  const [dimensionX1, setDimensionX1] = useState<string | undefined>(
    props?.data?.measurementAttributes?.dimensionX1?.toString(),
  );
  const [linerSqFt, setLinerSqFt] = useState<string | undefined>(
    props?.data?.measurementAttributes?.linerSqFt?.toString(),
  );
  const [overlapCalc, setOverlapCalc] = useState<string | undefined>(
    props?.data?.measurementAttributes?.overlapCalc?.toString(),
  );
  const [overlapOverride, setOverlapOverride] = useState<string | undefined>(
    props?.data?.measurementAttributes?.overlapOverride?.toString(),
  );
  // #endregion

  // #region projectAttributes
  const [projectAttributes, setProjectAttributes] = useState(
    props?.data?.projectAttributes,
  );
  const [brand, setBrand] = useState<string | undefined>(
    props?.data?.projectAttributes?.brand,
  );
  const [dropShip, setDropShip] = useState(props?.data?.projectAttributes?.dropShip);
  const [enteredBy, setEnteredBy] = useState<string | undefined>(
    props?.data?.projectAttributes?.enteredBy,
  );
  const [enteredDate, setEnteredDate] = useState<string | undefined>(
    props?.data?.projectAttributes?.enteredDate,
  );
  const [externalComments, setExternalComments] = useState<string | undefined>(
    props?.data?.projectAttributes?.externalComments,
  );
  const [freightInstructions, setFreightInstructions] = useState<string | undefined>(
    props?.data?.projectAttributes?.freightInstructions,
  );
  const [geolocationMatch, setGeolocationMatch] = useState(
    props?.data?.projectAttributes?.geolocationMatch,
  );
  const [lathamQuoteNumber, setLathamQuoteNumber] = useState<string | undefined>(
    props?.data?.projectAttributes?.lathamQuoteNumber,
  );
  const [orderChanges, setOrderChanges] = useState(
    props?.data?.projectAttributes?.orderChanges,
  );
  const [pickup, setPickup] = useState(
    state?.spec?.projectAttributes?.pickup ?? props?.data?.projectAttributes?.pickup,
  );
  const [poNumber, setPoNumber] = useState<string | undefined>(
    state?.spec?.projectAttributes?.poNumber ??
      props?.data?.projectAttributes?.poNumber,
  );
  const [previousJobNumber, setPreviousJobNumber] = useState<string | undefined>(
    props?.data?.projectAttributes?.previousJobNumber,
  );
  const [priorityRush, setPriorityRush] = useState(
    props?.data?.projectAttributes?.priorityRush,
  );
  const [productType, setProductType] = useState<string | undefined>(
    props?.data?.projectAttributes?.productType,
  );
  const [projectId, setProjectId] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectId,
  );
  const [projectLat, setProjectLat] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectLat,
  );
  const [projectLong, setProjectLong] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectLong,
  );
  const [projectName, setProjectName] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectName,
  );
  const [projectSiteCity, setProjectSiteCity] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectSiteCity,
  );
  const [projectSiteState, setProjectSiteState] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectSiteState,
  );
  const [projectSiteStreet, setProjectSiteStreet] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectSiteStreet,
  );
  const [projectSiteStreet2, setProjectSiteStreet2] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectSiteStreet2,
  );
  const [projectSiteZip, setProjectSiteZip] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectSiteZip,
  );
  const [projectUrl, setProjectUrl] = useState<string | undefined>(
    props?.data?.projectAttributes?.projectUrl,
  );
  const [quoteOrder, setQuoteOrder] = useState<string | undefined>(
    props?.data?.projectAttributes?.quoteOrder,
  );
  const [quoteStatus, setQuoteStatus] = useState<string | undefined>(
    props?.data?.projectAttributes?.quoteStatus,
  );
  const [reorderRemakeReason, setReorderRemakeReason] = useState<string | undefined>(
    props?.data?.projectAttributes?.reorderRemakeReason,
  );
  const [reorderSerialNumber, setReorderSerialNumber] = useState<string | undefined>(
    props?.data?.projectAttributes?.reorderSerialNumber,
  );
  const [shipToCity, setShipToCity] = useState<string | undefined>(
    state?.spec?.projectAttributes?.shipToCity ??
      props?.data?.projectAttributes?.shipToCity,
  );
  const [shipToCountry, setShipToCountry] = useState<string | undefined>(
    state?.spec?.projectAttributes?.shipToCountry ??
      props?.data?.projectAttributes?.shipToCountry,
  );
  const [shipToState, setShipToState] = useState<string | undefined>(
    state?.spec?.projectAttributes?.shipToState ??
      props?.data?.projectAttributes?.shipToState,
  );
  const [shipToStreet, setShipToStreet] = useState<string | undefined>(
    state?.spec?.projectAttributes?.shipToStreet ??
      props?.data?.projectAttributes?.shipToStreet,
  );
  const [shipToZip, setShipToZip] = useState<string | undefined>(
    state?.spec?.projectAttributes?.shipToZip ??
      props?.data?.projectAttributes?.shipToZip,
  );
  // #endregion

  // #region shapeAttributes
  const [shapeAttributes, setShapeAttributes] = useState(props?.data?.shapeAttributes);
  const [bottomType, setBottomType] = useState<string | undefined>(
    props?.data?.shapeAttributes?.bottomType,
  );
  const [freeformIdentification, setFreeformIdentification] = useState(
    props?.data?.shapeAttributes?.freeformIdentification,
  );
  const [shapeId, setShapeId] = useState<string | undefined>(
    props?.data?.shapeAttributes?.shapeId,
  );
  const [shapeName, setShapeName] = useState<string | undefined>(
    props?.data?.shapeAttributes?.shapeName,
  );
  const [shapeOrientation, setShapeOrientation] = useState<string | undefined>(
    props?.data?.shapeAttributes?.shapeOrientation,
  );
  // #endregion

  // Validation state
  const [validFields, setValidFields] = useState<SpecFormFieldsValidationType>(
    InitialSpecFormValidationStatus(),
  );
  const currentValues = {
    spec,
    coverFeatureAttributes,
    vinylCoveredSteps,
    coverMaterialAttributes,
    customerAttributes,
    deckingAttributes,
    fullPerimeterCoverAccessoryAttributes,
    geoShapeAttributes,
    linerMaterialAttributes,
    linerAccessoryAttributes,
    measurementAttributes,
    projectAttributes,
    shapeAttributes,
  };

  const updateOrderState = () => {
    dispatch({
      type: "updateSpec",
      payload: spec,
    });
  };

  // setSpec
  useEffect(() => {
    setSpec({
      ...spec,
      coverMaterialAttributes: coverMaterialAttributes,
      linerMaterialAttributes: linerMaterialAttributes,
      linerAccessoryAttributes: linerAccessoryAttributes,
      customerAttributes: customerAttributes,
      deckingAttributes: deckingAttributes,
      fullPerimeterCoverAccessoryAttributes: fullPerimeterCoverAccessoryAttributes,
      geoShapeAttributes: geoShapeAttributes,
      measurementAttributes: measurementAttributes,
      projectAttributes: projectAttributes,
      shapeAttributes: shapeAttributes,
    });
  }, [
    coverMaterialAttributes,
    linerMaterialAttributes,
    linerAccessoryAttributes,
    customerAttributes,
    deckingAttributes,
    fullPerimeterCoverAccessoryAttributes,
    geoShapeAttributes,
    measurementAttributes,
    projectAttributes,
    shapeAttributes,
  ]);

  // setCoverMaterialAttributes
  useEffect(() => {
    if (isCover) {
      setCoverMaterialAttributes({
        ...coverMaterialAttributes,
        generalSpecific: generalSpecific ?? "",
        materialColor: materialColor ?? "",
        materialDrainTypeFeatureSId: materialDrainTypeFeatureSId ?? "",
        materialFabric: materialFabric ?? "",
        materialGrid: parseInt(materialGrid ?? "0"),
        materialSpringUpgradeFeatureSId: materialSpringUpgradeFeatureSId ?? "",
      });
    }
  }, [
    generalSpecific,
    materialColor,
    materialDrainTypeFeatureSId,
    materialFabric,
    materialGrid,
    materialSpringUpgradeFeatureSId,
  ]);

  // setLinerMaterialAttributes
  useEffect(() => {
    if (!isCover) {
      setLinerMaterialAttributes({
        ...linerMaterialAttributes,
        generalSpecific: generalSpecific ?? "",
        materialWall: materialWall ?? "",
        materialFloor: materialFloor ?? "",
        materialBeadType: materialBeadType ?? "",
      });
    }
  }, [generalSpecific, materialWall, materialFloor, materialBeadType]);

  // setlinerAccessoryAttributes
  useEffect(() => {
    if (!isCover) {
      setlinerAccessoryAttributes({
        ...linerAccessoryAttributes,
        safetyStripFeatureSId: safetyStripFeatureSId ?? "",
        safetyStripeWidth: parseInt(safetyStripeWidth ?? "0"),
        materialSolidShallowFeatureSId: materialSolidShallowFeatureSId ?? "",
        safetyStripeColor: safetyStripeColor ?? "",
        materialOverlapFeatureId: materialOverlapFeatureId ?? "",
        materialOverlapSize: parseInt(materialOverlapSize ?? "0"),
        materialSolidShallowColor: materialSolidShallowColor ?? "",
        racingLanesFeatureSId: racingLanesFeatureSId ?? "",
        racingLanesComments: racingLanesComments ?? "",
        targetFeatureSId: targetFeatureSId ?? "",
        targetComments: targetComments ?? "",
      });
    }
  }, [
    safetyStripFeatureSId,
    safetyStripeWidth,
    materialSolidShallowFeatureSId,
    safetyStripeColor,
    materialOverlapFeatureId,
    materialOverlapSize,
    materialSolidShallowColor,
    racingLanesFeatureSId,
    racingLanesComments,
    targetFeatureSId,
    targetComments,
  ]);

  // setCustomerAttributes
  useEffect(() => {
    setCustomerAttributes({
      ...customerAttributes,
      SugarId: sugarId ?? "",
      customerName: customerName ?? "",
      gl_company_new_c: gl_company_new_c ?? "",
      indirect_customer_number_c: indirect_customer_number_c ?? "",
      purchasing_channel_c: purchasing_channel_c ?? "",
      rewards_id_c: rewards_id_c ?? "",
      selectedBranchNumber: selectedBranchNumber ?? "",
      selectedCustomerNumber: selectedCustomerNumber ?? "",
    });
  }, [
    sugarId,
    customerName,
    gl_company_new_c,
    indirect_customer_number_c,
    purchasing_channel_c,
    rewards_id_c,
    selectedBranchNumber,
    selectedCustomerNumber,
  ]);

  // setDeckingAttributes
  useEffect(() => {
    if (isCover) {
      setDeckingAttributes({
        ...deckingAttributes,
        copingTexture: copingTexture ?? "",
        deckAnchorFeatureSId: deckAnchorFeatureSId ?? "",
        deckConsistency: deckConsistency,
        deckSize: parseInt(deckSize ?? "0"),
        deckType: deckType ?? "",
        extraPaddingFeatureId: extraPaddingFeatureId ?? "",
        extraPaddingLength: parseInt(extraPaddingLength ?? "0"),
        partialDeckingArray: partialDeckingArray,
      });
    }
  }, [
    copingTexture,
    deckAnchorFeatureSId,
    deckConsistency,
    deckSize,
    deckType,
    extraPaddingFeatureId,
    extraPaddingLength,
    partialDeckingArray,
  ]);

  // setFullPerimeterCoverAccessoryAttributes
  useEffect(() => {
    setFullPerimeterCoverAccessoryAttributes({
      ...fullPerimeterCoverAccessoryAttributes,
      brassAnchorCollarsFeatureSId: brassAnchorCollarsFeatureSId ?? "",
      bucklesFeatureSId: bucklesFeatureSId ?? "",
      cableAssembly: {
        cableArea1: {
          cableArea1FeatureId: cableArea1FeatureId ?? "",
          cableArea1Length: parseInt(cableArea1Length ?? "0"),
        },
        cableArea2: {
          cableArea2FeatureId: cableArea2FeatureId ?? "",
          cableArea2Length: parseInt(cableArea2Length ?? "0"),
        },
        cableArea3: {
          cableArea3FeatureId: cableArea3FeatureId ?? "",
          cableArea3Length: parseInt(cableArea3Length ?? "0"),
        },
        cableArea4: {
          cableArea4FeatureId: cableArea4FeatureId ?? "",
          cableArea4Length: parseInt(cableArea4Length ?? "0"),
        },
      },
      doubleDRingsFeatureSId: doubleDRingsFeatureSId ?? "",
      extraLongAnchorsFeatureSId: extraLongAnchorsFeatureSId ?? "",
      kemKapFeatureSId: kemKapFeatureSId ?? "",
      noAnchorsFeatureSId: noAnchorsFeatureSId ?? "",
      noHardwareFeatureSId: noHardwareFeatureSId ?? "",
      topBottomWebbingFeatureSId: topBottomWebbingFeatureSId ?? "",
      yStrapAssembly: {
        yStrapFeatureId: yStrapFeatureId ?? "",
        yStrapQty: parseInt(yStrapQty ?? "0"),
      },
      singlePad: {
        singlePadFeatureId: singlePadFeatureId ?? "",
        singlePadQty: parseInt(singlePadQty ?? "0"),
      },
    });
  }, [
    brassAnchorCollarsFeatureSId,
    bucklesFeatureSId,
    cableArea1FeatureId,
    cableArea1Length,
    cableArea2FeatureId,
    cableArea2Length,
    cableArea3FeatureId,
    cableArea3Length,
    cableArea4FeatureId,
    cableArea4Length,
    doubleDRingsFeatureSId,
    extraLongAnchorsFeatureSId,
    kemKapFeatureSId,
    noAnchorsFeatureSId,
    noHardwareFeatureSId,
    topBottomWebbingFeatureSId,
    yStrapFeatureId,
    yStrapQty,
    singlePadFeatureId,
    singlePadQty,
  ]);

  // setGeoShapeAttributes
  useEffect(() => {
    setGeoShapeAttributes({
      ...geoShapeAttributes,
      cornerLC: {
        cornerType: cornerLCCornerType ?? "",
        cornerSize: parseInt(cornerLCCornerSize ?? "0"),
      },
      cornerLD: {
        cornerType: cornerLDCornerType ?? "",
        cornerSize: parseInt(cornerLDCornerSize ?? "0"),
      },
      cornerLS: {
        cornerType: cornerLSCornerType ?? "",
        cornerSize: parseInt(cornerLSCornerSize ?? "0"),
      },
      cornerRD: {
        cornerType: cornerRDCornerType ?? "",
        cornerSize: parseInt(cornerRDCornerSize ?? "0"),
      },
      cornerRS: {
        cornerType: cornerRSCornerType ?? "",
        cornerSize: parseInt(cornerRSCornerSize ?? "0"),
      },
      cornerReverse: {
        cornerType: cornerReverseCornerType ?? "",
        cornerSize: parseInt(cornerReverseCornerSize ?? "0"),
      },
      cutCorner: {
        cutCornerQty: parseInt(cutCornerQty ?? "0"),
        cutCornerFeatureId: cutCornerFeatureId ?? "",
      },
      ovalEnd: {
        ovalEndQty: parseInt(ovalEndQty ?? "0"),
        ovalEndFeatureId: ovalEndFeatureId ?? "",
      },
      romanEnd: {
        romanEndQty: parseInt(romanEndQty ?? "0"),
        romanEndFeatureId: romanEndFeatureId ?? "",
      },
    });
  }, [
    cornerLCCornerType,
    cornerLCCornerSize,
    cornerLDCornerType,
    cornerLDCornerSize,
    cornerLSCornerType,
    cornerLSCornerSize,
    cornerRDCornerType,
    cornerRDCornerSize,
    cornerRSCornerType,
    cornerRSCornerSize,
    cornerReverseCornerType,
    cornerReverseCornerSize,
    cutCornerQty,
    cutCornerFeatureId,
    ovalEndQty,
    ovalEndFeatureId,
    romanEndQty,
    romanEndFeatureId,
  ]);

  // setMeasurementAttributes
  useEffect(() => {
    setMeasurementAttributes({
      ...measurementAttributes,
      coverLnFt: parseInt(coverLnFt ?? "0"),
      coverSqFt: parseInt(coverSqFt ?? "0"),
      dimensionA: parseInt(dimensionA ?? "0"),
      dimensionA1: parseInt(dimensionA1 ?? "0"),
      dimensionB: parseInt(dimensionB ?? "0"),
      dimensionB3: parseInt(dimensionB3 ?? "0"),
      dimensionB7: parseInt(dimensionB7 ?? "0"),
      dimensionC: parseInt(dimensionC ?? "0"),
      dimensionD: parseInt(dimensionD ?? "0"),
      dimensionE: parseInt(dimensionE ?? "0"),
      dimensionJ2: parseInt(dimensionJ2 ?? "0"),
      dimensionJ3: parseInt(dimensionJ3 ?? "0"),
      dimensionJ4: parseInt(dimensionJ4 ?? "0"),
      dimensionL: parseInt(dimensionL ?? "0"),
      dimensionRd: parseInt(dimensionRd ?? "0"),
      dimensionS: parseInt(dimensionS ?? "0"),
      dimensionS1: parseInt(dimensionS1 ?? "0"),
      dimensionT: parseInt(dimensionT ?? "0"),
      dimensionV3: parseInt(dimensionV3 ?? "0"),
      dimensionW1: parseInt(dimensionW1 ?? "0"),
      dimensionX1: parseInt(dimensionX1 ?? "0"),
      linerSqFt: parseInt(linerSqFt ?? "0"),
      overlapCalc: parseInt(overlapCalc ?? "0"),
      overlapOverride: parseInt(overlapOverride ?? "0"),
    });
  }, [
    coverLnFt,
    coverSqFt,
    dimensionA,
    dimensionA1,
    dimensionB,
    dimensionB3,
    dimensionB7,
    dimensionC,
    dimensionD,
    dimensionE,
    dimensionJ2,
    dimensionJ3,
    dimensionJ4,
    dimensionL,
    dimensionRd,
    dimensionS,
    dimensionS1,
    dimensionT,
    dimensionV3,
    dimensionW1,
    dimensionX1,
    linerSqFt,
    overlapCalc,
    overlapOverride,
  ]);

  // setProjectAttributes
  useEffect(() => {
    setProjectAttributes({
      ...projectAttributes,
      brand: brand ?? "",
      dropShip: dropShip,
      enteredBy: enteredBy ?? "",
      enteredDate: enteredDate ?? "",
      externalComments: externalComments ?? "",
      freightInstructions: freightInstructions ?? "",
      geolocationMatch: geolocationMatch,
      lathamQuoteNumber: lathamQuoteNumber ?? "",
      orderChanges: orderChanges,
      pickup: pickup,
      poNumber: poNumber ?? "",
      previousJobNumber: previousJobNumber ?? "",
      priorityRush: priorityRush,
      productType: productType ?? "",
      projectId: projectId ?? "",
      projectLat: projectLat ?? "",
      projectLong: projectLong ?? "",
      projectName: projectName ?? "",
      projectSiteCity: projectSiteCity ?? "",
      projectSiteState: projectSiteState ?? "",
      projectSiteStreet: projectSiteStreet ?? "",
      projectSiteStreet2: projectSiteStreet2 ?? "",
      projectSiteZip: projectSiteZip ?? "",
      projectUrl: projectUrl ?? "",
      quoteOrder: quoteOrder ?? "",
      quoteStatus: quoteStatus ?? "",
      reorderRemakeReason: reorderRemakeReason ?? "",
      reorderSerialNumber: reorderSerialNumber ?? "",
      shipToCity: shipToCity ?? "",
      shipToCountry: shipToCountry ?? "",
      shipToState: shipToCountry ?? "",
      shipToStreet: shipToStreet ?? "",
      shipToZip: shipToZip ?? "",
    });
  }, [
    brand,
    dropShip,
    enteredBy,
    enteredDate,
    externalComments,
    freightInstructions,
    geolocationMatch,
    lathamQuoteNumber,
    orderChanges,
    pickup,
    poNumber,
    previousJobNumber,
    priorityRush,
    productType,
    projectId,
    projectLat,
    projectLong,
    projectName,
    projectSiteCity,
    projectSiteState,
    projectSiteStreet,
    projectSiteStreet2,
    projectSiteZip,
    projectUrl,
    quoteOrder,
    quoteStatus,
    reorderRemakeReason,
    reorderSerialNumber,
    shipToCity,
    shipToCountry,
    shipToState,
    shipToStreet,
    shipToZip,
  ]);

  // setShapeAttributes
  useEffect(() => {
    setShapeAttributes({
      ...shapeAttributes,
      bottomType: bottomType ?? "",
      freeformIdentification: freeformIdentification,
      shapeId: shapeId ?? "",
      shapeName: shapeName ?? "",
      shapeOrientation: shapeOrientation ?? "",
    });
  }, [bottomType, freeformIdentification, shapeId, shapeName, shapeOrientation]);

  const setObstructionTypeArray = (obs: ObstructionTypeArray, i: number) => {
    if (obstructionTypeArray) obstructionTypeArray[i] = obs;
  };

  const setOutsideStepArray = (obs: OutsideStepArray, i: number) => {
    if (outsideStepArray) outsideStepArray[i] = obs;
  };

  const setVinylCoveredStepInfo = (step: VinylCoveredStepsInfo, i: number) => {
    if (vinylCoveredStepsInfo) vinylCoveredStepsInfo[i] = step;
  };

  const setPartialDeckingArray = (deck: PartialDecking, i: number) => {
    if (partialDeckingArray) partialDeckingArray[i] = deck;
  };

  const addFeature = () => {
    if (featureType === FeatureType.Steps) {
      outsideStepArray?.push(newFeature as OutsideStepArray);
    } else {
      obstructionTypeArray?.push(newFeature as ObstructionTypeArray);
    }
    setFeatModalOpen(false);
  };

  return (
    <div className={"spec-form"}>
      <OrderForm
        values={currentValues}
        initialValidationState={InitialSpecFormValidationStatus()}
        schema={SpecFormSchema}
        onFieldsValid={updateOrderState}
        onFieldsInvalid={updateOrderState}
        setValidFields={
          setValidFields as React.Dispatch<
            React.SetStateAction<GenericFieldValidationType>
          >
        }
      >
        <>
          {isCover && (
            <>
              <Modal
                title={t("component.forms.spec.addNewFeature", "Add New Feature")}
                isOpen={featModalOpen}
                actions={
                  <UserModalActions
                    onCancel={() => {
                      setFeatModalOpen(false);
                    }}
                    onSubmit={addFeature}
                    disabled={featureType === ""}
                  />
                }
                size={"small"}
                onClose={() => {
                  setFeatModalOpen(false);
                }}
                className={"nopad"}
              >
                <div className={"spec-form__modal"}>
                  <ManagedSelect
                    label={t("component.forms.spec.featureType", "Feature Type")}
                    isValid={featureType !== ""}
                    value={featureType}
                    onChange={setFeatureType}
                    options={featureTypeOptions}
                    name={"new_feature"}
                  />
                  {featureType !== FeatureType.Steps && featureType !== "" && (
                    <Obstruction
                      obstruction={null}
                      setObstruction={(obs) => {
                        setNewFeature(obs);
                      }}
                    />
                  )}
                  {featureType === FeatureType.Steps && (
                    <OutsideStep
                      outsideStep={null}
                      setOutsideStep={(obs) => {
                        setNewFeature(obs);
                      }}
                    />
                  )}
                </div>
              </Modal>
              <Collapsible
                title={t("component.forms.spec.featureData", "Feature Data")}
              >
                <>
                  <Button
                    primary
                    onClick={() => {
                      setFeatModalOpen(true);
                    }}
                  >
                    {t("component.forms.spec.addFeature", "Add Feature")}
                  </Button>
                  {obstructionTypeArray && obstructionTypeArray.length > 0 && (
                    <>
                      {obstructionTypeArray.map((val, i) => {
                        return (
                          <Obstruction
                            key={`obstruction-${i}`}
                            obstruction={val}
                            setObstruction={(obs) => {
                              setObstructionTypeArray(obs, i);
                            }}
                          />
                        );
                      })}
                    </>
                  )}
                  {outsideStepArray && outsideStepArray.length > 0 && (
                    <>
                      {outsideStepArray.map((val, i) => {
                        return (
                          <OutsideStep
                            key={`outsideStep-${i}`}
                            outsideStep={val}
                            setOutsideStep={(obs) => {
                              setOutsideStepArray(obs, i);
                            }}
                          />
                        );
                      })}
                    </>
                  )}
                </>
              </Collapsible>
            </>
          )}
          {!isCover && (
            <Collapsible title={t("component.forms.spec.featureData", "Feature Data")}>
              <>
                {obstructionTypeArray && obstructionTypeArray.length > 0 && (
                  <>
                    {obstructionTypeArray.map((val, i) => {
                      return (
                        <Obstruction
                          key={`obstruction-${i}`}
                          obstruction={val}
                          setObstruction={(obs) => {
                            setObstructionTypeArray(obs, i);
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </>
            </Collapsible>
          )}
          {/* Vinyl Covered Step Attributes */}
          {!isCover && (
            <Collapsible
              title={t("component.forms.spec.vinylCoveredSteps", "Vinyl Covered Steps")}
            >
              <>
                {vinylCoveredStepsInfo && vinylCoveredStepsInfo.length > 0 && (
                  <>
                    {vinylCoveredStepsInfo.map((val, i) => {
                      return (
                        <VinylCoveredStepInfo
                          key={`obstruction-${i}`}
                          vinylCoveredStepInfo={val}
                          setVinylCoveredStepInfo={(step) => {
                            setVinylCoveredStepInfo(step, i);
                          }}
                        />
                      );
                    })}
                  </>
                )}
              </>
            </Collapsible>
          )}
          {/* Cover Material Attributes */}
          {isCover && (
            <Collapsible
              title={t(
                "component.forms.spec.coverMaterialAttributes",
                "Cover Material Attributes",
              )}
            >
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.generalSpecific", "General Specific")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.generalSpecific",
                  "General Specific",
                )}
                onChange={setGeneralSpecific}
                value={generalSpecific}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialColor", "Material Color")}
                type={"text"}
                placeholder={t("component.forms.spec.materialColor", "Material Color")}
                onChange={setMaterialColor}
                value={materialColor}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.materialDrainTypeFeatureSId",
                  "Material Drain Type FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialDrainTypeFeatureSId",
                  "Material Drain Type FeatureSId",
                )}
                onChange={setMaterialDrainTypeFeatureSId}
                value={materialDrainTypeFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialFabric", "Material Fabric")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialFabric",
                  "Material Fabric",
                )}
                onChange={setMaterialFabric}
                value={materialFabric}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialGrid", "Material Grid")}
                type={"text"}
                placeholder={t("component.forms.spec.materialGrid", "Material Grid")}
                onChange={setMaterialGrid}
                value={materialGrid}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.materialSpringUpgradeFeatureSId",
                  "Material Spring Upgrade FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialSpringUpgradeFeatureSId",
                  "Material Spring Upgrade FeatureSId",
                )}
                onChange={setMaterialSpringUpgradeFeatureSId}
                value={materialSpringUpgradeFeatureSId}
                disabled={disabled}
              />
            </Collapsible>
          )}
          {/* Liner Material Attributes */}
          {!isCover && (
            <Collapsible
              title={t(
                "component.forms.spec.linerMaterialAttributes",
                "Liner Material Attributes",
              )}
            >
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.generalSpecific", "General Specific")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.generalSpecific",
                  "General Specific",
                )}
                onChange={setGeneralSpecific}
                value={generalSpecific}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialWall", "Material Wall")}
                type={"text"}
                placeholder={t("component.forms.spec.materialWall", "Material Wall")}
                onChange={setMaterialWall}
                value={materialWall}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialFloor", "Material Floor")}
                type={"text"}
                placeholder={t("component.forms.spec.materialFloor", "Material Floor")}
                onChange={setMaterialFloor}
                value={materialFloor}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.materialBeadType", "Material Bead Type")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialBeadType",
                  "Material Bead Type",
                )}
                onChange={setMaterialBeadType}
                value={materialBeadType}
                disabled={disabled}
              />
            </Collapsible>
          )}
          {/* Liner Accessory Attributes */}
          {!isCover && (
            <Collapsible
              title={t(
                "component.forms.spec.linerAccessoryAttributes",
                "Liner Accessory Attributes",
              )}
            >
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.safetyStripFeatureSId",
                  "Safety Strip FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.safetyStripFeatureSId",
                  "Safety Strip FeatureSId",
                )}
                onChange={setSafetyStripFeatureSId}
                value={safetyStripFeatureSId}
                disabled={true}
              />
              <>
                {state.accessories.safetyStripes &&
                  state.accessories.safetyStripeWidth && (
                    <ManagedInput
                      isValid={true}
                      label={t(
                        "component.forms.spec.safetyStripeWidth",
                        "Safety Stripe Width",
                      )}
                      type={"text"}
                      placeholder={t(
                        "component.forms.spec.safetyStripeWidth",
                        "Safety Stripe Width",
                      )}
                      onChange={setSafetyStripeWidth}
                      value={safetyStripeWidth}
                      disabled={disabled}
                    />
                  )}
              </>
              <>
                {state.accessories.safetyStripes && safetyStripeColor && (
                  <ManagedSelect
                    label={t(
                      "component.forms.spec.safetyStripesColor",
                      "Safety Stripes Color",
                    )}
                    isValid={true}
                    value={safetyStripeColor}
                    onChange={setSafetyStripeColor}
                    options={SafetyStripesOptions}
                    name={"safetyStripesColor"}
                    disabled={disabled}
                  />
                )}
              </>
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.materialOverlapFeatureId",
                  "Material Overlap FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialOverlapFeatureId",
                  "Material Overlap FeatureId",
                )}
                onChange={setMaterialOverlapFeatureId}
                value={materialOverlapFeatureId}
                disabled={true}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.materialOverlapSize",
                  "Material Overlap Size",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialOverlapSize",
                  "Material Overlap Size",
                )}
                onChange={setMaterialOverlapSize}
                value={materialOverlapSize}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.materialSolidShallowFeatureSId",
                  "Material Solid Shallow FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.materialSolidShallowFeatureSId",
                  "Material Solid Shallow FeatureSId",
                )}
                onChange={setMaterialSolidShallowFeatureSId}
                value={materialSolidShallowFeatureSId}
                disabled={true}
              />
              <ManagedSelect
                label={t(
                  "component.forms.spec.materialSolidShallowColor",
                  "Material Solid Shallow Color",
                )}
                isValid={true}
                value={materialSolidShallowColor}
                onChange={setMaterialSolidShallowColor}
                options={SolidShallowEndOptions}
                name={"materialSolidShallowColor"}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.racingLanesFeatureSId",
                  "Racing Lanes FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.racingLanesFeatureSId",
                  "Racing Lanes FeatureSId",
                )}
                onChange={setRacingLanesFeatureSId}
                value={racingLanesFeatureSId}
                disabled={true}
              />

              <ManagedInput
                label={t(
                  "component.forms.spec.racingLanesComments",
                  "Racing Lanes Comments",
                )}
                isValid={true}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.racingLanesComments",
                  "Racing Lanes Comments",
                )}
                onChange={setRacingLanesComments}
                value={racingLanesComments}
              />

              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.targetFeatureSId", "Target FeatureSId")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.targetFeatureSId",
                  "Target FeatureSId",
                )}
                onChange={setTargetFeatureSId}
                value={targetFeatureSId}
                disabled={true}
              />
              <ManagedInput
                label={t("component.forms.spec.targetComments", "Target Comments")}
                isValid={true}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.targetComments",
                  "Target Comments",
                )}
                onChange={setTargetComments}
                value={targetComments}
              />
            </Collapsible>
          )}
          {/* Customer Attributes */}
          <Collapsible
            title={t("component.forms.spec.customerAttributes", "Customer Attributes")}
          >
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.sugarId", "Sugar Id")}
              type={"text"}
              placeholder={t("component.forms.spec.sugarId", "Sugar Id")}
              onChange={setSugarId}
              value={sugarId}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.customerName", "Customer Name")}
              type={"text"}
              placeholder={t("component.forms.spec.customerName", "Customer Name")}
              onChange={setCustomerName}
              value={customerName}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.glCompanyNewC", "gl_company_new_c")} // no idea what this means
              type={"text"}
              placeholder={t("component.forms.spec.glCompanyNewC", "gl_company_new_c")} // no idea what this means
              onChange={setGl_company_new_c}
              value={gl_company_new_c}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.indirectCustomerNumber",
                "Indirect Customer Number",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.indirectCustomerNumber",
                "Indirect Customer Number",
              )}
              onChange={setIndirect_customer_number_c}
              value={indirect_customer_number_c}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.purchasingChannel", "Purchasing Channel")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.purchasingChannel",
                "Purchasing Channel",
              )}
              onChange={setPurchasing_channel_c}
              value={purchasing_channel_c}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.rewardsId", "Rewards Id")}
              type={"text"}
              placeholder={t("component.forms.spec.rewardsId", "Rewards Id")}
              onChange={setRewards_id_c}
              value={rewards_id_c}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.selectedBranchNumber",
                "Selected Branch Number",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.selectedBranchNumber",
                "Selected Branch Number",
              )}
              onChange={setSelectedBranchNumber}
              value={selectedBranchNumber}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.selectedCustomerNumber",
                "Selected Customer Number",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.selectedCustomerNumber",
                "Selected Customer Number",
              )}
              onChange={setSelectedCustomerNumber}
              value={selectedCustomerNumber}
              disabled={disabled}
            />
          </Collapsible>
          {/* Decking Attributes */}
          <Collapsible
            title={t("component.forms.spec.deckingAttributes", "Decking Attributes")}
          >
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.copingTexture", "Coping Texture")}
              type={"text"}
              placeholder={t("component.forms.spec.copingTexture", "Coping Texture")}
              onChange={setCopingTexture}
              value={copingTexture}
              disabled
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.deckAnchorFeatureSId",
                "Deck Anchor FeatureSId",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.deckAnchorFeatureSId",
                "Deck Anchor FeatureSId",
              )}
              onChange={setDeckAnchorFeatureSId}
              value={deckAnchorFeatureSId}
              disabled
            />
            <ManagedToggle
              label={t(
                "component.forms.spec.deckConsistencyRough",
                "Deck Consistency Rough?",
              )}
              name={"Deck Consistency Rough?"}
              isValid={true}
              onChange={setDeckConsistency}
              checked={deckConsistency}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.deckSize", "Deck Size")}
              type={"text"}
              placeholder={t("component.forms.spec.deckSize", "Deck Size")}
              onChange={setDeckSize}
              value={deckSize}
              disabled
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.deckType", "Deck Type")}
              type={"text"}
              placeholder={t("component.forms.spec.deckType", "Deck Type")}
              onChange={setDeckType}
              value={deckType}
              disabled
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.extraPaddingFeatureId",
                "Extra Padding FeatureId",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.extraPaddingFeatureId",
                "Extra Padding FeatureId",
              )}
              onChange={setExtraPaddingFeatureId}
              value={extraPaddingFeatureId}
              disabled
            />
            <>
              {partialDeckingArray && partialDeckingArray.length > 0 && (
                <>
                  {partialDeckingArray.map((val, i) => {
                    return (
                      <PartialDeckingFeat
                        key={`partial-decking-${i}`}
                        partialDecking={val}
                        setPartialDecking={(obs) => {
                          setPartialDeckingArray(obs, i);
                        }}
                      />
                    );
                  })}
                </>
              )}
            </>
          </Collapsible>
          {/* Full Perimeter Cover Accessory Attributes */}
          {isCover && (
            <Collapsible
              title={t(
                "component.forms.spec.fullPerimeterCoverAccessoryAttributes",
                "Full Perimeter Cover Accessory Attributes",
              )}
            >
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.brassAnchorCollarsFeatureSId",
                  "Brass Anchor Collars FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.brassAnchorCollarsFeatureSId",
                  "Brass Anchor Collars FeatureSId",
                )}
                onChange={setBrassAnchorCollarsFeatureSId}
                value={brassAnchorCollarsFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.bucklesFeatureSId",
                  "Buckles FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.bucklesFeatureSId",
                  "Buckles FeatureSId",
                )}
                onChange={setBucklesFeatureSId}
                value={bucklesFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea1FeatureId",
                  "Cable Area 1 FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea1FeatureId",
                  "Cable Area 1 FeatureId",
                )}
                onChange={setCableArea1FeatureId}
                value={cableArea1FeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea1Length",
                  "Cable Area 1 Length",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea1Length",
                  "Cable Area 1 Length",
                )}
                onChange={setCableArea1Length}
                value={cableArea1Length}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea2FeatureId",
                  "Cable Area 2 FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea2FeatureId",
                  "Cable Area 2 FeatureId",
                )}
                onChange={setCableArea2FeatureId}
                value={cableArea2FeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea2Length",
                  "Cable Area 2 Length",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea2Length",
                  "Cable Area 2 Length",
                )}
                onChange={setCableArea2Length}
                value={cableArea2Length}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea3FeatureId",
                  "Cable Area 3 FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea3FeatureId",
                  "Cable Area 3 FeatureId",
                )}
                onChange={setCableArea3FeatureId}
                value={cableArea3FeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea3Length",
                  "Cable Area 3 Length",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea3Length",
                  "Cable Area 3 Length",
                )}
                onChange={setCableArea3Length}
                value={cableArea3Length}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea4FeatureId",
                  "Cable Area 4 FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea4FeatureId",
                  "Cable Area 4 FeatureId",
                )}
                onChange={setCableArea4FeatureId}
                value={cableArea4FeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.cableArea4Length",
                  "Cable Area 4 Length",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.cableArea4Length",
                  "Cable Area 4 Length",
                )}
                onChange={setCableArea4Length}
                value={cableArea4Length}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.doubleDRingsFeatureSId",
                  "Double D Rings FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.doubleDRingsFeatureSId",
                  "Double D Rings FeatureSId",
                )}
                onChange={setDoubleDRingsFeatureSId}
                value={doubleDRingsFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.extraLongAnchorsFeatureSId",
                  "Extra Long Anchors FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.extraLongAnchorsFeatureSId",
                  "Extra Long Anchors FeatureSId",
                )}
                onChange={setExtraLongAnchorsFeatureSId}
                value={extraLongAnchorsFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.kemKapFeatureSId", "Kem Kap FeatureSId")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.kemKapFeatureSId",
                  "Kem Kap FeatureSId",
                )}
                onChange={setKemKapFeatureSId}
                value={kemKapFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.noAnchorsFeatureSId",
                  "No Anchors FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.noAnchorsFeatureSId",
                  "No Anchors FeatureSId",
                )}
                onChange={setNoAnchorsFeatureSId}
                value={noAnchorsFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.noHardwareFeatureSId",
                  "No Hardware FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.noHardwareFeatureSId",
                  "No Hardware FeatureSId",
                )}
                onChange={setNoHardwareFeatureSId}
                value={noHardwareFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.topBottomWebbingFeatureSId",
                  "Top Bottom Webbing FeatureSId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.topBottomWebbingFeatureSId",
                  "Top Bottom Webbing FeatureSId",
                )}
                onChange={setTopBottomWebbingFeatureSId}
                value={topBottomWebbingFeatureSId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.yStrapFeatureID", "Y Strap FeatureID")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.yStrapFeatureID",
                  "Y Strap FeatureID",
                )}
                onChange={setYStrapFeatureId}
                value={yStrapFeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t("component.forms.spec.yStrapQuantity", "Y Strap Quantity")}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.yStrapQuantity",
                  "Y Strap Quantity",
                )}
                onChange={setYStrapQty}
                value={yStrapQty}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.singlePadFeatureId",
                  "Single Pad FeatureId",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.singlePadFeatureId",
                  "Single Pad FeatureId",
                )}
                onChange={setSinglePadFeatureId}
                value={singlePadFeatureId}
                disabled={disabled}
              />
              <ManagedInput
                isValid={true}
                label={t(
                  "component.forms.spec.singlePadQuantity",
                  "Single Pad Quantity",
                )}
                type={"text"}
                placeholder={t(
                  "component.forms.spec.singlePadQuantity",
                  "Single Pad Quantity",
                )}
                onChange={setSinglePadQty}
                value={singlePadQty}
                disabled={disabled}
              />
            </Collapsible>
          )}
          {/* Geo Shape Attributes */}
          <Collapsible
            title={t(
              "component.forms.spec.geometricShapeAttributes",
              "Geometric Shape Attributes",
            )}
          >
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLCCornerType",
                "Corner LC Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLCCornerType",
                "Corner LC Corner Type",
              )}
              onChange={setCornerLCCornerType}
              value={cornerLCCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLCCornerSize",
                "Corner LC Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLCCornerSize",
                "Corner LC Corner Size",
              )}
              onChange={setCornerLCCornerSize}
              value={cornerLCCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLDCornerType",
                "Corner LD Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLDCornerType",
                "Corner LD Corner Type",
              )}
              onChange={setCornerLDCornerType}
              value={cornerLDCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLDCornerSize",
                "Corner LD Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLDCornerSize",
                "Corner LD Corner Size",
              )}
              onChange={setCornerLDCornerSize}
              value={cornerLDCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLSCornerType",
                "Corner LS Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLSCornerType",
                "Corner LS Corner Type",
              )}
              onChange={setCornerLSCornerType}
              value={cornerLSCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerLSCornerSize",
                "Corner LS Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerLSCornerSize",
                "Corner LS Corner Size",
              )}
              onChange={setCornerLSCornerSize}
              value={cornerLSCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerRDCornerType",
                "Corner RD Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerRDCornerType",
                "Corner RD Corner Type",
              )}
              onChange={setCornerRDCornerType}
              value={cornerRDCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerRDCornerSize",
                "Corner RD Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerRDCornerSize",
                "Corner RD Corner Size",
              )}
              onChange={setCornerRDCornerSize}
              value={cornerRDCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerRSCornerType",
                "Corner RS Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerRSCornerType",
                "Corner RS Corner Type",
              )}
              onChange={setCornerRSCornerType}
              value={cornerRSCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerRSCornerSize",
                "Corner RS Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerRSCornerSize",
                "Corner RS Corner Size",
              )}
              onChange={setCornerRSCornerSize}
              value={cornerRSCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerReverseCornerType",
                "Corner Reverse Corner Type",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerReverseCornerType",
                "Corner Reverse Corner Type",
              )}
              onChange={setCornerReverseCornerType}
              value={cornerReverseCornerType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cornerReverseCornerSize",
                "Corner Reverse Corner Size",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cornerReverseCornerSize",
                "Corner Reverse Corner Size",
              )}
              onChange={setCornerReverseCornerSize}
              value={cornerReverseCornerSize}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.cutCornerQuantity", "Cut Corner Quantity")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cutCornerQuantity",
                "Cut Corner Quantity",
              )}
              onChange={setCutCornerQty}
              value={cutCornerQty}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.cutCornerFeatureId",
                "Cut Corner FeatureId",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.cutCornerFeatureId",
                "Cut Corner FeatureId",
              )}
              onChange={setCutCornerFeatureId}
              value={cutCornerFeatureId}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.ovalEndQuantity", "Oval End Quantity")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.ovalEndQuantity",
                "Oval End Quantity",
              )}
              onChange={setOvalEndQty}
              value={ovalEndQty}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.ovalEndFeatureId", "Oval End FeatureId")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.ovalEndFeatureId",
                "Oval End FeatureId",
              )}
              onChange={setOvalEndFeatureId}
              value={ovalEndFeatureId}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.romanEndQuantity", "Roman End Quantity")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.romanEndQuantity",
                "Roman End Quantity",
              )}
              onChange={setRomanEndQty}
              value={romanEndQty}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.romanEndFeatureId", "Roman End FeatureId")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.romanEndFeatureId",
                "Roman End FeatureId",
              )}
              onChange={setRomanEndFeatureId}
              value={romanEndFeatureId}
              disabled={disabled}
            />
          </Collapsible>
          {/* Measurement Attributes */}
          <Collapsible
            title={t(
              "component.forms.spec.measurementAttributes",
              "Measurement Attributes",
            )}
          >
            <>
              {isCover && (
                <>
                  <ManagedInput
                    isValid={true}
                    label={t(
                      "component.forms.spec.coverLinearFeet",
                      "Cover Linear Feet",
                    )}
                    type={"text"}
                    placeholder={t(
                      "component.forms.spec.coverLinearFeet",
                      "Cover Linear Feet",
                    )}
                    onChange={setCoverLnFt}
                    value={coverLnFt}
                    disabled={disabled}
                  />
                  <ManagedInput
                    isValid={true}
                    label={t(
                      "component.forms.spec.coverSquareFeet",
                      "Cover Square Feet",
                    )}
                    type={"text"}
                    placeholder={t(
                      "component.forms.spec.coverSquareFeet",
                      "Cover Square Feet",
                    )}
                    onChange={setCoverSqFt}
                    value={coverSqFt}
                    disabled={disabled}
                  />
                </>
              )}
            </>
            <>
              {!isCover && (
                <ManagedInput
                  isValid={true}
                  label={t("component.forms.spec.linerSquareFeet", "Liner Square Feet")}
                  type={"text"}
                  placeholder={t(
                    "component.forms.spec.linerSquareFeet",
                    "Liner Square Feet",
                  )}
                  onChange={setLinerSqFt}
                  value={linerSqFt}
                  disabled={disabled}
                />
              )}
            </>
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionA", "Dimension A")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionA", "Dimension A")}
              onChange={setDimensionA}
              value={dimensionA}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionA1", "Dimension A1")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionA1", "Dimension A1")}
              onChange={setDimensionA1}
              value={dimensionA1}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionB", "Dimension B")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionB", "Dimension B")}
              onChange={setDimensionB}
              value={dimensionB}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionB3", "Dimension B3")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionB3", "Dimension B3")}
              onChange={setDimensionB3}
              value={dimensionB3}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionB7", "Dimension B7")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionB7", "Dimension B7")}
              onChange={setDimensionB7}
              value={dimensionB7}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionC", "Dimension C")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionC", "Dimension C")}
              onChange={setDimensionC}
              value={dimensionC}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionD", "Dimension D")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionD", "Dimension D")}
              onChange={setDimensionD}
              value={dimensionD}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionE", "Dimension E")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionE", "Dimension E")}
              onChange={setDimensionE}
              value={dimensionE}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionJ2", "Dimension J2")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionJ2", "Dimension J2")}
              onChange={setDimensionJ2}
              value={dimensionJ2}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionJ3", "Dimension J3")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionJ3", "Dimension J3")}
              onChange={setDimensionJ3}
              value={dimensionJ3}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionJ4", "Dimension J4")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionJ4", "Dimension J4")}
              onChange={setDimensionJ4}
              value={dimensionJ4}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionL", "Dimension L")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionL", "Dimension L")}
              onChange={setDimensionL}
              value={dimensionL}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionRd", "Dimension Rd")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionRd", "Dimension Rd")}
              onChange={setDimensionRd}
              value={dimensionRd}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionS", "Dimension S")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionS", "Dimension S")}
              onChange={setDimensionS}
              value={dimensionS}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionS1", "Dimension S1")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionS1", "Dimension S1")}
              onChange={setDimensionS1}
              value={dimensionS1}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionT", "Dimension T")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionT", "Dimension T")}
              onChange={setDimensionT}
              value={dimensionT}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionV3", "Dimension V3")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionV3", "Dimension V3")}
              onChange={setDimensionV3}
              value={dimensionV3}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionW1", "Dimension W1")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionW1", "Dimension W1")}
              onChange={setDimensionW1}
              value={dimensionW1}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.dimensionX1", "Dimension X1")}
              type={"text"}
              placeholder={t("component.forms.spec.dimensionX1", "Dimension X1")}
              onChange={setDimensionX1}
              value={dimensionX1}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.overlapCalculator", "Overlap Calculator")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.overlapCalculator",
                "Overlap Calculator",
              )}
              onChange={setOverlapCalc}
              value={overlapCalc}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.overlapOverride", "Overlap Override")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.overlapOverride",
                "Overlap Override",
              )}
              onChange={setOverlapOverride}
              value={overlapOverride}
              disabled={disabled}
            />
          </Collapsible>
          {/* Project Attributes */}
          <Collapsible
            title={t("component.forms.spec.projectAttributes", "Project Attributes")}
          >
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.brand", "Brand")}
              type={"text"}
              placeholder={t("component.forms.spec.brand", "Brand")}
              onChange={setBrand}
              value={brand}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.dropShip", "Drop Ship?")}
              name={"Drop Ship?"}
              isValid={true}
              onChange={setDropShip}
              checked={dropShip}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.enteredBy", "Entered By")}
              type={"text"}
              placeholder={t("component.forms.spec.enteredBy", "Entered By")}
              onChange={setEnteredBy}
              value={enteredBy}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.enteredDate", "Entered Date")}
              type={"text"}
              placeholder={t("component.forms.spec.enteredDate", "Entered Date")}
              onChange={setEnteredDate}
              value={enteredDate}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.externalComments", "External Comments")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.externalComments",
                "External Comments",
              )}
              onChange={setExternalComments}
              value={externalComments}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.freightInstructions",
                "Freight Instructions",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.freightInstructions",
                "Freight Instructions",
              )}
              onChange={setFreightInstructions}
              value={freightInstructions}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.geolocationMatch", "Geolocation Match?")}
              name={t("component.forms.spec.geolocationMatch", "Geolocation Match?")}
              isValid={true}
              onChange={setGeolocationMatch}
              checked={geolocationMatch}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.lathamQuoteNumber", "Latham Quote Number")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.lathamQuoteNumber",
                "Latham Quote Number",
              )}
              onChange={setLathamQuoteNumber}
              value={lathamQuoteNumber}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.orderChanges", "Order Changes?")}
              name={"Order Changes?"}
              isValid={true}
              onChange={setOrderChanges}
              checked={orderChanges}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.pickup", "Pickup?")}
              name={"Pickup?"}
              isValid={true}
              onChange={setPickup}
              checked={pickup}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.poNumber", "poNumber")}
              type={"text"}
              placeholder={t("component.forms.spec.poNumber", "poNumber")}
              onChange={setPoNumber}
              value={poNumber}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.previousJobNumber", "Previous Job Number")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.previousJobNumber",
                "Previous Job Number",
              )}
              onChange={setPreviousJobNumber}
              value={previousJobNumber}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.priorityRush", "Priority Rush?")}
              name={"Priority Rush?"}
              isValid={true}
              onChange={setPriorityRush}
              checked={priorityRush}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.productType", "Product Type")}
              type={"text"}
              placeholder={t("component.forms.spec.productType", "Product Type")}
              onChange={setProductType}
              value={productType}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectId", "ProjectId")}
              type={"text"}
              placeholder={t("component.forms.spec.projectId", "ProjectId")}
              onChange={setProjectId}
              value={projectId}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectLatitude", "Project Latitude")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectLatitude",
                "Project Latitude",
              )}
              onChange={setProjectLat}
              value={projectLat}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectLongitude", "Project Longitude")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectLongitude",
                "Project Longitude",
              )}
              onChange={setProjectLong}
              value={projectLong}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectName", "Project Name")}
              type={"text"}
              placeholder={t("component.forms.spec.projectName", "Project Name")}
              onChange={setProjectName}
              value={projectName}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectSiteCity", "Project Site City")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectSiteCity",
                "Project Site City",
              )}
              onChange={setProjectSiteCity}
              value={projectSiteCity}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectSiteState", "Project Site State")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectSiteState",
                "Project Site State",
              )}
              onChange={setProjectSiteState}
              value={projectSiteState}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectSiteStreet", "Project Site Street")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectSiteStreet",
                "Project Site Street",
              )}
              onChange={setProjectSiteStreet}
              value={projectSiteStreet}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.projectSiteStreet2",
                "Project Site Street 2",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.projectSiteStreet2",
                "Project Site Street 2",
              )}
              onChange={setProjectSiteStreet2}
              value={projectSiteStreet2}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectSiteZip", "Project Site Zip")}
              type={"text"}
              placeholder={t("component.forms.spec.projectSiteZip", "Project Site Zip")}
              onChange={setProjectSiteZip}
              value={projectSiteZip}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.projectUrl", "Project Url")}
              type={"text"}
              placeholder={t("component.forms.spec.projectUrl", "Project Url")}
              onChange={setProjectUrl}
              value={projectUrl}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.quoteOrder", "Quote Order")}
              type={"text"}
              placeholder={t("component.forms.spec.quoteOrder", "Quote Order")}
              onChange={setQuoteOrder}
              value={quoteOrder}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.quoteStatus", "Quote Status")}
              type={"text"}
              placeholder={t("component.forms.spec.quoteStatus", "Quote Status")}
              onChange={setQuoteStatus}
              value={quoteStatus}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.reorderRemakeReason",
                "Reorder Remake Reason",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.reorderRemakeReason",
                "Reorder Remake Reason",
              )}
              onChange={setReorderRemakeReason}
              value={reorderRemakeReason}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t(
                "component.forms.spec.reorderSerialNumber",
                "Reorder Serial Number",
              )}
              type={"text"}
              placeholder={t(
                "component.forms.spec.reorderSerialNumber",
                "Reorder Serial Number",
              )}
              onChange={setReorderSerialNumber}
              value={reorderSerialNumber}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shipToCity", "Ship To City")}
              type={"text"}
              placeholder={t("component.forms.spec.shipToCity", "Ship To City")}
              onChange={setShipToCity}
              value={shipToCity}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shipToCountry", "Ship To Country")}
              type={"text"}
              placeholder={t("component.forms.spec.shipToCountry", "Ship To Country")}
              onChange={setShipToCountry}
              value={shipToCountry}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shipToState", "Ship To State")}
              type={"text"}
              placeholder={t("component.forms.spec.shipToState", "Ship To State")}
              onChange={setShipToState}
              value={shipToState}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shipToStreet", "Ship To Street")}
              type={"text"}
              placeholder={t("component.forms.spec.shipToStreet", "Ship To Street")}
              onChange={setShipToStreet}
              value={shipToStreet}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shipToZip", "Ship To Zip")}
              type={"text"}
              placeholder={t("component.forms.spec.shipToZip", "Ship To Zip")}
              onChange={setShipToZip}
              value={shipToZip}
              disabled={disabled}
            />
          </Collapsible>
          {/* Shape Attributes */}
          <Collapsible
            title={t("component.forms.spec.shapeAttributes", "Shape Attributes")}
          >
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.bottomType", "Bottom Type")}
              type={"text"}
              placeholder={t("component.forms.spec.bottomType", "Bottom Type")}
              onChange={setBottomType}
              value={bottomType}
              disabled={disabled}
            />
            <ManagedToggle
              label={t("component.forms.spec.freeform", "Freeform?")}
              name={"Freeform?"}
              isValid={true}
              onChange={setFreeformIdentification}
              checked={freeformIdentification}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shapeId", "ShapeId")}
              type={"text"}
              placeholder={t("component.forms.spec.shapeId", "ShapeId")}
              onChange={setShapeId}
              value={shapeId}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shapeName", "Shape Name")}
              type={"text"}
              placeholder={t("component.forms.spec.shapeName", "Shape Name")}
              onChange={setShapeName}
              value={shapeName}
              disabled={disabled}
            />
            <ManagedInput
              isValid={true}
              label={t("component.forms.spec.shapeOrientation", "Shape Orientation")}
              type={"text"}
              placeholder={t(
                "component.forms.spec.shapeOrientation",
                "Shape Orientation",
              )}
              onChange={setShapeOrientation}
              value={shapeOrientation}
              disabled={disabled}
            />
          </Collapsible>
        </>
      </OrderForm>
    </div>
  );
};

export default SpecForm;
